import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { io, Socket } from "socket.io-client";
import { customLogger } from "../utils/Logger";
import { storageService } from "../services/storageService";
import { STORAGE_KEYS } from "../constants";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

interface SocketProviderProps {
  readonly children: React.ReactNode;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});
const url = process.env.REACT_APP_SOCKET_URL;

const subscribeToStorage = (callback: () => void): (() => void) => {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
};

const getUdid = (): string | null => storageService.getUdid();

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const uuid = useSyncExternalStore(subscribeToStorage, getUdid);

  const initSocket = useCallback(() => {
    if (!url) return;
    if (!uuid) {
      customLogger("[Socket] No udid in storage — connection deferred.");
      return;
    }
    customLogger("[Socket] Initializing with udid:", uuid);
    const newSocket: Socket = io(url, {
      timeout: 3000,
      reconnection: true,
      autoConnect: false,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      extraHeaders: {
        deviceudid: uuid,
      },
    });
    newSocket.on("connect", () => {
      customLogger("✅ [Socket] Connected: ", newSocket.id);
      setIsConnected(true);
    });
    newSocket.on("disconnect", (reason: Socket.DisconnectReason) => {
      customLogger("🔌 [Socket] Disconnected. Reason:", reason);
      setIsConnected(false);
    });
    newSocket.on("connect_error", (err: Error) => {
      customLogger("❌ [Socket] Connection error:", err);
      setIsConnected(false);
    });
    newSocket.connect();
    setSocket(newSocket);
    return newSocket;
  }, [uuid]);
  useEffect(() => {
    const newSocket = initSocket();
    return () => {
      newSocket?.removeAllListeners();
      newSocket?.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [uuid, initSocket]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
